import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef } from 'react';
import { cx } from '../../../internal/cx.js';
import { Button } from '../../actions/button/index.js';
import { CopyButton } from '../../actions/copy-button/index.js';
import { EmptyState } from '../empty-state/index.js';
import { ErrorGlyph, LockGlyph, OfflineGlyph, SearchGlyph, WarningGlyph, WrenchGlyph, } from '../../../internal/icons.js';
/** Kinds where a retry cannot change the outcome, so no retry button is shown. */
const NO_RETRY = new Set(['forbidden', 'not-found']);
/**
 * Maps an HTTP status to an `ErrorKind`. Pure and framework-free: pass the
 * status from your own API client. `0`, `undefined` with `offline`, or
 * `offline: true` give `offline`; 401 and 403 give `forbidden`; 404 and 410
 * give `not-found`; 5xx give `server`; everything else gives `error`.
 * `maintenance` is never inferred: the product decides when it applies.
 */
export function errorKindFromStatus(status, options = {}) {
    if (options.offline || status === 0)
        return 'offline';
    if (status == null)
        return 'error';
    if (status === 401 || status === 403)
        return 'forbidden';
    if (status === 404 || status === 410)
        return 'not-found';
    if (status >= 500 && status <= 599)
        return 'server';
    return 'error';
}
const KIND_ICON = {
    error: () => _jsx(WarningGlyph, {}),
    forbidden: () => _jsx(LockGlyph, {}),
    'not-found': () => _jsx(SearchGlyph, {}),
    offline: () => _jsx(OfflineGlyph, {}),
    server: () => _jsx(ErrorGlyph, {}),
    maintenance: () => _jsx(WrenchGlyph, {}),
};
/** The default decorative icon for an error kind (used by ErrorState and ErrorPage). */
export function errorKindIcon(kind) {
    return KIND_ICON[kind]();
}
/** Something failed to load. Say what failed and offer a retry. */
export const ErrorState = forwardRef(function ErrorState({ title, description, onRetry, retryLabel = 'Try again', retrying = false, actions, icon, kind = 'error', requestId, requestIdLabel = 'Request ID', className, children, ...rest }, ref) {
    const canRetry = onRetry && !NO_RETRY.has(kind);
    return (_jsxs(EmptyState, { ref: ref, ...rest, role: "alert", className: cx('mn-error-state', className), "data-kind": kind === 'error' ? undefined : kind, icon: icon ?? errorKindIcon(kind), title: title, description: description, actions: canRetry || actions ? (_jsxs(_Fragment, { children: [canRetry ? (_jsx(Button, { variant: "primary", loading: retrying, onClick: onRetry, children: retryLabel })) : null, actions] })) : undefined, children: [children, requestId ? (_jsxs("p", { className: "mn-error-state__request-id", children: [_jsxs("span", { children: [requestIdLabel, ":"] }), " ", _jsx("code", { children: requestId }), _jsx(CopyButton, { value: requestId, iconOnly: true, size: "xs", label: `Copy ${requestIdLabel}` })] })) : null] }));
});
//# sourceMappingURL=error-state.js.map